import { useCallback, useState } from 'react';
import { toast } from 'react-toastify';
import { FileWithPath } from 'react-dropzone';
import { useErrorHandler, APIError, useQueryClient, ClientError, useLoaderToast, useJobStatus } from 'shared-ui';

import {
  addOwnersWithPhotos,
  addOwnersWithPhotosImportFile,
  addOwnersWithPhotosImportPhotos,
  addOwnersWithPhotosValidateFileWithPhotos,
} from 'api/owners/owners';
import { useMutation } from 'hooks/useMutation/useMutation';
import { useLocale } from 'hooks/useLocale/useLocale';
import { AppMessages } from 'i18n/messages';
import { QueryKeyEnum } from 'core/global.enum';
import { ValidateOwnersImportFileErrorResponse } from 'api/owners/owners.types';
import { isPkdPhotoImportJobResponse, isPkdPhotoImportSyncResponse } from 'app/owners/utils/ownerImportHelpers';

import { AddOwnerImportWithPhoto } from './AddOwnerImportWithPhoto';
import { AddOwnerImportWithPhotoContainerProps } from './AddOwnerImportWithPhoto.types';

export const AddOwnerImportWithPhotoContainer = ({
  isModalOpen,
  onModalClose,
  onClickBackButton,
}: AddOwnerImportWithPhotoContainerProps) => {
  const { formatMessage } = useLocale();
  const queryClient = useQueryClient();

  const { load: loadAddOwnerWithPhotos, close: closeAddOwnersWithPhotos } = useLoaderToast('addOwnerWithPhotos');
  const { handleError } = useErrorHandler();

  const handleJobComplete = () => {
    queryClient.invalidateQueries([QueryKeyEnum.OWNERS]);
  };

  const handleJobError = (error: string) => {
    toast.error(error || formatMessage({ id: AppMessages['common.error'] }));
  };

  const jobStatus = useJobStatus({
    useGlobalModal: true,
    autoClose: false,
    onComplete: handleJobComplete,
    onError: handleJobError,
    title: formatMessage({ id: AppMessages['owners.addOwner.importWithPhoto.loadingText'] }),
    description: formatMessage({ id: AppMessages['owners.addOwner.importWithPhoto.importFile.description'] }),
  });

  const [file, setFile] = useState<FileWithPath>();
  const [photos, setPhotos] = useState<FileWithPath[]>();
  const [fileError, setFileError] = useState<string>();
  const [photosError, setPhotosError] = useState<string>();
  const [importFileName, setImportFileName] = useState<string>();
  const [downloadLink, setDownloadLink] = useState('');
  const [filesNotMatch, setFilesNotMatch] = useState(false);

  const handleFileErrors = ({ response }: ClientError<APIError<ValidateOwnersImportFileErrorResponse>>) => {
    if (response?.data.type === 'pkds_limit') {
      setFileError(response?.data?.error);
      return;
    }

    createDownloadLink(response?.data?.message);
  };

  const { mutate: validateFileWithPhotos, isLoading: isLoadingValidateFileWithPhotos } = useMutation(
    addOwnersWithPhotosValidateFileWithPhotos,
    {
      onSuccess: ({ filesNotMatch, bledyImportu }) => {
        setFilesNotMatch(filesNotMatch);

        if (bledyImportu) {
          createDownloadLink(bledyImportu);
        } else {
          setDownloadLink('');
          setFilesNotMatch(false);
        }
      },
      onError: (error: unknown) => handleError({ error }),
    },
  );

  const { mutate: onAddFile, isLoading: isLoadingAddFile } = useMutation(addOwnersWithPhotosImportFile, {
    onSuccess: (response, file) => {
      setFile(file);
      setImportFileName(response.importFileName);
    },
    onError: handleFileErrors,
  });

  // Sequential chunked upload: the backend endpoint appends each batch to the
  // same upload folder, so multiple POSTs are equivalent to one big POST.
  // Chunk size matches the multer `files: 100` limit; nginx proxy-body-size is
  // 500m which comfortably covers 100 × ~500 KB photos.
  const PHOTO_UPLOAD_CHUNK_SIZE = 100;
  const [isBatchUploading, setIsBatchUploading] = useState(false);
  const { mutateAsync: mutateAsyncAddPhotos, isLoading: isLoadingAddPhotos } = useMutation(
    addOwnersWithPhotosImportPhotos,
    {
      // Errors from a single chunk are surfaced via onAddPhotosBatched below so
      // we do not double-handle here.
      handleErrorManually: true,
    },
  );

  const onAddPhotos = useCallback(
    async (photos: FileWithPath[]) => {
      if (photos.length === 0) return;

      setIsBatchUploading(true);
      try {
        for (let i = 0; i < photos.length; i += PHOTO_UPLOAD_CHUNK_SIZE) {
          const chunk = photos.slice(i, i + PHOTO_UPLOAD_CHUNK_SIZE);
          await mutateAsyncAddPhotos(chunk);
        }
        setPhotos(photos);
        if (importFileName) validateFileWithPhotos(importFileName);
      } catch (error) {
        handleError({ error });
      } finally {
        setIsBatchUploading(false);
      }
    },
    [mutateAsyncAddPhotos, importFileName, validateFileWithPhotos, handleError],
  );

  const { mutate: onAddOwnersWithPhotos, isLoading: isLoadingAddOwnersWithPhotos } = useMutation(addOwnersWithPhotos, {
    onMutate: () =>
      loadAddOwnerWithPhotos(formatMessage({ id: AppMessages['owners.addOwner.importWithPhoto.loadingText'] })),
    onSuccess: (response) => {
      try {
        closeAddOwnersWithPhotos();
        setDownloadLink('');
        setFilesNotMatch(false);

        if (!response) {
          onModalClose();
          return;
        }

        if (isPkdPhotoImportJobResponse(response)) {
          jobStatus.startJob(response.jobId);

          onModalClose();
          return;
        }

        if (isPkdPhotoImportSyncResponse(response)) {
          if (response.bledyImportu) {
            createDownloadLink(response.bledyImportu);
          }

          if (response.filesNotMatch) {
            setFilesNotMatch(true);
          }

          queryClient.invalidateQueries([QueryKeyEnum.OWNERS]);

          onModalClose();
          return;
        }

        onModalClose();
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        toast.error(errorMessage);
        onModalClose();
      }
    },
    onError: (error: unknown) => handleError({ error }),
    onSettled: () => closeAddOwnersWithPhotos(),
  });

  const createDownloadLink = (uploadErrors?: string) => {
    if (!uploadErrors) return;

    const data = new Blob([uploadErrors], { type: 'text/plain' });

    if (downloadLink !== '') window.URL.revokeObjectURL(downloadLink);

    setDownloadLink(window.URL.createObjectURL(data));
  };

  const onSubmit = () => {
    if (!importFileName) return;

    onAddOwnersWithPhotos(importFileName);
  };

  return (
    <>
      <AddOwnerImportWithPhoto
        isModalOpen={isModalOpen}
        onModalClose={onModalClose}
        onClickBackButton={onClickBackButton}
        onAddFile={onAddFile}
        onAddPhotos={onAddPhotos}
        isLoadingAddFile={isLoadingAddFile}
        isLoadingAddPhotos={isLoadingAddPhotos || isBatchUploading || isLoadingValidateFileWithPhotos}
        file={file}
        setFile={setFile}
        photos={photos}
        setPhotos={setPhotos}
        fileError={fileError}
        setFileError={setFileError}
        photosError={photosError}
        setPhotosError={setPhotosError}
        downloadLink={downloadLink}
        setDownloadLink={setDownloadLink}
        filesNotMatch={filesNotMatch}
        setFilesNotMatch={setFilesNotMatch}
        onSubmit={onSubmit}
        isLoadingAddOwnersWithPhotos={isLoadingAddOwnersWithPhotos}
      />
    </>
  );
};
